import {
  GcfPriceDisplayFormat,
  ScaleConfigBase,
  ScaleType,
} from 'src/features/components/editComponentPage/forms/FormSelector.interface';
import { lang } from 'src/utils';

import { DyProductSource } from './Component';
import { ComponentTypes } from './ComponentTypesList';
import { GCFConfig } from './Environment';
import { IThemes, ProjectConfigThemesSchema } from './GCFThemes';
import { IGcfOutageStampConfig } from './Product';

export enum productPriceCurrencyPositionEnum {
  before = 'before',
  after = 'after',
}

interface ProductPriceSoldoutSticker {
  backgroundColor: string;
  textColor: string;
  textValue: string;
}

type ProductNameField =
  | 'Name'
  | 'Short Name'
  | 'Long Name'
  | 'DT Name'
  | 'COD Name'
  | 'Short Monitor Name'
  | 'Summary Monitor Name'
  | 'CSO Name';

export enum OutageStrategyEnum {
  'Soldout' = 'soldout-sticker',
  'Elimination' = 'elimination',
  'Replacement' = 'replacement',
  'Back soon' = 'soldout-stamp',
}

export enum PriceIntegrationSourceEnum {
  PRICE_POSTER = 'price poster',
  RESTAURANT_BRIDGE = 'restaurant bridge',
}

enum ProductPriceSizeEnum {
  regular = 'regular',
  large = 'large',
}

interface Nutrition {
  active: boolean;
  label: string;
}

export enum ScaleTypesEnum {
  FC = 'FC',
  FC_DARK_MODE = 'FC_DARK_MODE',
  DT = 'DT',
  DT_DARK_MODE = 'DT_DARK_MODE',
  DTLISTING = 'DTLISTING',
  DTLISTING_DARK_MODE = 'DTLISTING_DARK_MODE',
}

enum FontStyleTypes {
  FONT_STYLE_REGULAR = 'regular',
  FONT_STYLE_BOLD = 'bold',
}

enum CodPositions {
  LEFT = 'left',
  RIGHT = 'right',
}

type FontStyleType =
  | FontStyleTypes.FONT_STYLE_REGULAR
  | FontStyleTypes.FONT_STYLE_BOLD;

interface BaseProjectFontsInterface {
  uid?: string;
  name: string;
  font_file: string;
  font_zip: string;
  font_style?: FontStyleType;
  default_typeface?: boolean;
}

export enum productDisclaimerPositionEnum {
  withProductText = 'withProductText', // Shows Product Level Disclaimers and Component Level Disclaimers Seperately , needed in MCD US
  withComponentDisclaimer = 'withComponentDisclaimer', // Shows Product Level Disclaimers with Component Level Disclaimers , Like we have in IOMBU/GCF 1.0
}

/**
 * @deprecated
 */
interface ScaleTypesInterface {
  [scaleTypeName: string]: ScaleType;
}

interface FontsAndThemeScaleInterface {
  fontSize: number;
  lineHeight: number;
}

interface FontsAndThemeScaleConfigInterface {
  [key: string]: FontsAndThemeScaleInterface;
}

interface ProductDisclaimerInterface {
  active: boolean; //Activate or Deactivate Product Level Disclaimer Globally
  type: productDisclaimerPositionEnum; //Works when Dislcaimer is active
  style?: GcfPriceDisplayFormat; // for product disclaimer
}

interface CustomizedOutageConfigInterface extends ScaleConfigBase {
  opacity?: string;
  textValue: string;
  strikethroughThickness?: string;
  strikethroughColor?: string;
  stamp?: IGcfOutageStampConfig;
}

export interface GlobalComponentsInterface {
  componentType: string;
  componentVariant?: string;
  states: string[];
  label: string;
  color: string;
}

export interface GlobalComponentsScreensInterface {
  [key: string]: GlobalComponentsScreenInterface[];
}

export interface GlobalComponentsScreenInterface {
  screenNumber: number;
  screenLayoutType: string;
  globalComponents: ScreenGlobalComponentInterface[];
}

export interface ScreenGlobalComponentInterface {
  componentType: ComponentTypes;
  componentVariant?: DyProductSource;
  screenSequence: number;
  enabled: boolean;
}

export interface ProjectConfigInterface {
  gcf: GCFConfig;
  productPriceDecimalSeparator?: string;
  productPriceIntegerSeparator?: string;
  productPriceCalorieSeparator: string;
  productPriceSeparator: string;
  productPriceCurrency?: string;
  productPriceCurrencyPosition?: productPriceCurrencyPositionEnum;
  productPriceSoldoutSticker: ProductPriceSoldoutSticker;
  productNameField?: ProductNameField;
  outageStrategy: OutageStrategyEnum;
  priceIntegrationSource: PriceIntegrationSourceEnum;
  /* Deprecated. Only used in Legacy components */
  productPriceSize?: ProductPriceSizeEnum;
  productFullPriceOnly?: boolean;
  nutrition?: Nutrition;
  showZeroPrices?: boolean;
  currencySpace?: boolean;
  /**
   * @deprecated Use `activeTheme` instead.
   */
  scaleTypeKey?: ScaleTypesEnum;
  autoscale?: boolean;
  /**
   * @deprecated Use `themes` instead.
   */
  scaleTypes?: ScaleTypesInterface;
  activeTheme?: string;
  indoorDefaultTheme?: string;
  outdoorDefaultTheme?: string;
  mcCafeDefaultTheme?: string;
  digitalMerchandiserDefaultTheme?: string;
  themes?: IThemes;
  showGlobalProductDisclaimer?: ProductDisclaimerInterface;
  contentAnalytics?: boolean;
  fonts?: BaseProjectFontsInterface[];
  fontScale?: FontsAndThemeScaleConfigInterface;
  customizedOutageConfig?: CustomizedOutageConfigInterface;
  debug?: boolean;
  verbose?: boolean;
  rbStoreIntegration?: boolean;
  idmbPositionNumber?: number;
  odmbPositionNumber?: number;
  mcCafePositionNumber?: number;
  merchandiserPositionNumber?: number;
  codPosition?: CodPositions;
  globalComponents?: GlobalComponentsInterface[];
  globalComponentsScreens?: GlobalComponentsScreensInterface;
}

export const ProjectConfigSchema = {
  title: 'ProjectConfigSchema',
  description: 'JSON Schema for ProjectConfigSchema',
  type: 'object',
  properties: {
    productPriceDecimalSeparator: { type: 'string' },
    productPriceCalorieSeparator: { type: 'string' },
    productPriceSeparator: { type: 'string' },
    productPriceCurrency: { type: 'string' },
    productPriceCurrencyPosition: { type: 'string' },
    productPriceSoldoutSticker: {
      type: 'object',
      properties: {
        backgroundColor: {
          type: 'string',
          minLength: 1,
          errorMessage: { minLength: lang?.errors?.required },
        },
        textColor: {
          type: 'string',
          minLength: 1,
          errorMessage: { minLength: lang?.errors?.required },
        },
        textValue: {
          type: 'string',
          minLength: 1,
          errorMessage: { minLength: lang?.errors?.required },
        },
      },
      required: ['backgroundColor', 'textColor', 'textValue'],
    },
    productFullPriceOnly: { type: 'boolean' },
    priceIntegrationSource: {
      type: 'string',
      enum: [
        PriceIntegrationSourceEnum.PRICE_POSTER,
        PriceIntegrationSourceEnum.RESTAURANT_BRIDGE,
      ],
    },
    outageStrategy: {
      type: 'string',
      enum: [
        OutageStrategyEnum['Back soon'],
        OutageStrategyEnum.Soldout,
        OutageStrategyEnum.Elimination,
        OutageStrategyEnum.Replacement,
      ],
    },
    nutrition: {
      type: 'object',
      properties: {
        active: { type: 'boolean' },
        label: { type: 'string' },
      },
      if: {
        properties: { active: { const: true } },
      },
      then: {
        properties: {
          label: {
            type: 'string',
            minLength: 1,
            errorMessage: { minLength: lang?.errors?.required },
          },
        },
      },
      required: ['active', 'label'],
    },
    showZeroPrices: { type: 'boolean' },
    currencySpace: { type: 'boolean' },

    customizedOutageConfig: {
      type: 'object',
      properties: {
        opacity: { type: 'string' },
        textValue: {
          type: 'string',
          minLength: 1,
          maxLength: 20,
          errorMessage: {
            minLength: lang?.errors?.required,
            maxLength: lang?.errors?.maxLength20,
          },
        },
        color: { type: 'string' },
        font: { type: 'string' },
        fontSize: { type: 'string' },
        lineHeight: { type: 'string' },
        strikethroughThickness: { type: 'string' },
        strikethroughColor: { type: 'string' },
        stamp: {
          type: 'object',
          properties: {
            visible: { type: 'boolean' },
            url: {
              type: 'string',
              minLength: 1,
              errorMessage: {
                minLength: lang?.errors?.required,
              },
            },
            width: { type: 'string' },
            height: { type: 'string' },
            top: { type: 'string' },
            left: { type: 'string' },
          },
          required: ['visible', 'url'],
        },
      },
      required: ['textValue'],
    },
    idmbPositionNumber: {
      type: 'number',
      minimum: 1,
      maximum: 15,
      errorMessage: lang?.formErrorMsg?.minimumAndMaximum(1, 15),
    },
    odmbPositionNumber: {
      type: 'number',
      minimum: 1,
      maximum: 15,
      errorMessage: lang?.formErrorMsg?.minimumAndMaximum(1, 15),
    },
    mcCafePositionNumber: {
      type: 'number',
      minimum: 1,
      maximum: 15,
      errorMessage: lang?.formErrorMsg?.minimumAndMaximum(1, 15),
    },
    merchandiserPositionNumber: {
      type: 'number',
      minimum: 1,
      maximum: 15,
      errorMessage: lang?.formErrorMsg?.minimumAndMaximum(1, 15),
    },
    codPosition: {
      type: 'string',
      enum: [CodPositions.LEFT, CodPositions.RIGHT],
    },
  },
  required: [
    'productPriceDecimalSeparator',
    'productPriceCalorieSeparator',
    'productPriceSeparator',
    'productPriceCurrency',
    'productPriceSoldoutSticker',
    'outageStrategy',
    'priceIntegrationSource',
    'productPriceCurrencyPosition',
  ],
  additionalProperties: true,
};

export const AccessibilitySettingsSchema = {
  title: 'ProjectConfigSchema',
  description: 'JSON Schema for ProjectConfigSchema',
  type: 'object',
  properties: {
    autoscale: { type: 'boolean' },
    activeTheme: {
      type: 'string',
    },
    themes: {
      type: 'object',
      patternProperties: {
        '^.*$': {
          anyOf: [ProjectConfigThemesSchema],
        },
      },
    },
  },
  required: ['themes'],
  additionalProperties: true,
};

export const CircularCurrencies = [
  {
    value: '$',
    label:
      '$ - ARS, AUD, BBD, BMD, CAD, CLP, COP, CVE, FJD, GYD, KYD, MXN, NAD, NAD, NZD, SBD, SRD, TTD, USD, XCD, XPF',
  },
  { value: '€', label: '€ - EUR' },
  { value: 'Af', label: 'Af - AFN' },
  { value: 'Lek', label: 'Lek - ALL' },
  { value: 'دج', label: 'دج - DZD' },
  { value: 'Kz', label: 'Kz - AOA' },
  { value: '֏', label: '֏ - AMD' },
  { value: 'ƒ', label: 'ƒ - ANG, AWG' },
  { value: '£', label: '£ - EGP, FKP, GIP, JEP, LBP, SDG, SHP, SYP' },
  { value: 'ман', label: 'ман - AZN' },
  { value: '.د.ب', label: '.د.ب - BHD' },
  { value: '৳', label: '৳ - BDT' },
  { value: 'FBu', label: 'FBu - BIF' },
  { value: '៛', label: '៛ - KHR' },
  { value: 'FCFA', label: 'FCFA - XAF' },
  { value: 'CF', label: 'CF - KMF' },
  { value: '₡', label: '₡ - CRC' },
  { value: 'kn', label: 'kn - HRK' },
  { value: 'Kč', label: 'Kč - CZK' },
  { value: ',-', label: ',- - CZ' },
  { value: 'FC', label: 'FC - CDF' },
  { value: 'kr', label: 'kr - DKK, ISK, SEK' },
  { value: 'Fdj', label: 'Fdj - DJF' },
  { value: 'RD$', label: 'RD$ - DOP' },
  { value: 'L', label: 'L - LSL, SZL' },
  { value: 'Br', label: 'Br - ETB' },
  { value: 'ლ', label: 'ლ - GEL' },
  { value: '¢', label: '¢ - GHS' },
  { value: 'Q', label: 'Q - GTQ' },
  { value: 'FG', label: 'FG - GNF' },
  { value: 'د.ك', label: 'د.ك - KWD' },
  { value: 'лв', label: 'лв - BGN, KGS, KZT, UZS' },
  { value: 'KSh', label: 'KSh - KES' },
  { value: '₭', label: '₭ - LAK' },
  { value: 'ل.د', label: 'ل.د - LYD' },
  { value: 'CHF', label: 'CHF - CHF' },
  { value: 'MOP$', label: 'MOP$ - MOP' },
  { value: 'Ar', label: 'Ar - MGA' },
  { value: 'MK', label: 'MK - MWK' },
  { value: 'RM', label: 'RM - MYR' },
  { value: '.ރ', label: '.ރ - MVR' },
  { value: '₨', label: '₨ - MUR, NPR, PKR, SCR' },
  { value: 'B/.', label: 'B/. - PAB' },
  { value: 'K', label: 'K - PGK' },
  { value: 'Gs', label: 'Gs - PYG' },
  { value: 'S/.', label: 'S/. - PEN' },
  { value: '₱', label: '₱ - PHP' },
  { value: 'zł', label: 'zł - PLN' },
  { value: '﷼', label: '﷼ - IRR, QAR, SAR, YER' },
  { value: 'lei', label: 'lei - RON' },
  { value: 'руб', label: 'руб - RUB' },
  { value: 'ر.س', label: 'ر.س - RWF' },
  { value: 'WS$', label: 'WS$ - WST' },
  { value: 'R', label: 'R - ZAR' },
  { value: '¥', label: '¥ - JPY' },
  { value: '₩', label: '₩ - KRW' },
  { value: 'ينار', label: 'ينار - JOD' },
  { value: 'جنية', label: 'جنية - EGP' },
  { value: 'EGP', label: 'EGP - EGP' },
  { value: '₺', label: '₺ - TRY' },
  { value: '₴', label: '₴ - UAH' },
  { value: 'грн', label: 'грн - UAH' },
];
